import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import { getReportSectionVisibility } from '../../utils/permissions';
import Loader from '../../components/common/Loader';

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  gap: '16px',
};

const sectionHeadingStyle = {
  margin: '0 0 12px 0',
  fontSize: '13px',
  fontWeight: 700,
  letterSpacing: '0.06em',
  color: '#4a5568',
};

const ReportsDashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const reportsPrefix = location.pathname.startsWith('/employee') ? '/employee' : '/admin';
  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();
  const normalizedUserRole = useMemo(() => {
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(u?.role || '').trim().toLowerCase();
    } catch {
      return '';
    }
  }, []);

  const { showAssetReports, showPerformanceReports } = useMemo(
    () => getReportSectionVisibility(normalizedUserRole, rolePermissions),
    [normalizedUserRole, rolePermissions]
  );

  const assetReports = useMemo(
    () => [
      {
        title: 'Site Wise Report',
        description: 'View assets grouped by site and location.',
        icon: '🏢',
        path: `${reportsPrefix}/reports/site-wise`,
      },
      {
        title: 'Category Wise Report',
        description: 'Analyze assets by category and type.',
        icon: '📂',
        path: `${reportsPrefix}/reports/category-wise`,
      },
      {
        title: 'Employee History Report',
        description: 'See asset assignment history for employees.',
        icon: '👤',
        path: `${reportsPrefix}/reports/employee-history`,
      },
      {
        title: 'Asset History Report',
        description: 'Track full lifecycle and handover history of assets.',
        icon: '📜',
        path: `${reportsPrefix}/reports/asset-history`,
      },
    ],
    [reportsPrefix]
  );

  const performanceReports = useMemo(
    () => [
      {
        title: 'Employee Feedback Report',
        description: 'Review employee feedback scores across events.',
        icon: '📝',
        path: `${reportsPrefix}/employee-feedback-report`,
      },
      {
        title: 'Team Feedback Report',
        description: 'Review team feedback details across events and matches.',
        icon: '👥',
        path: `${reportsPrefix}/team-feedback-report`,
      },
      {
        title: 'Performance report',
        description: 'View overall performance insights and trends.',
        icon: '📈',
        path: `${reportsPrefix}/performance-report`,
      },
    ],
    [reportsPrefix]
  );

  const renderCards = (items) =>
    items.map((option, index) => (
      <div
        key={`${option.path}-${index}`}
        onClick={() => navigate(option.path)}
        style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          cursor: 'pointer',
          transition: 'all 0.2s',
          border: '2px solid transparent',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#667eea';
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow =
            '0 4px 12px rgba(102, 126, 234, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'transparent';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
        }}
      >
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>
          {option.icon}
        </div>
        <h3
          style={{
            margin: '0 0 8px 0',
            fontSize: '16px',
            fontWeight: 600,
            color: '#2d3748',
          }}
        >
          {option.title}
        </h3>
        <p
          style={{
            margin: 0,
            color: '#718096',
            fontSize: '13px',
          }}
        >
          {option.description}
        </p>
      </div>
    ));

  if (!permissionsReady) {
    return (
      <div className="reports-dashboard-page" style={{ padding: '16px' }}>
        <Loader />
      </div>
    );
  }

  return (
    <div className="reports-dashboard-page" style={{ padding: '16px' }}>
      {!showAssetReports && !showPerformanceReports && (
        <div
          style={{
            padding: '24px',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            color: '#4a5568',
            fontSize: '14px',
          }}
        >
          You do not have access to any report areas. Under Settings → User Roles → Reports, enable
          Asset Reports and/or Performance Reports for your role.
        </div>
      )}

      {showAssetReports && (
        <div style={{ marginBottom: '28px' }}>
          <h2 style={sectionHeadingStyle}>ASSET REPORTS</h2>
          <div style={gridStyle}>{renderCards(assetReports)}</div>
        </div>
      )}

      {showPerformanceReports && (
        <div>
          <h2 style={sectionHeadingStyle}>PERFORMANCE REPORTS</h2>
          <div style={gridStyle}>{renderCards(performanceReports)}</div>
        </div>
      )}
    </div>
  );
};

export default ReportsDashboardPage;


